/**
 * V10.7: CHURN PREVENTION DASHBOARD
 * Life & Wealth Solutions - Phoenix Rescue Apps
 * FIXED: Removed @/components/ui imports — self-contained Tailwind only
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Users, CheckCircle, Clock, RefreshCw, X } from 'lucide-react';

const ChurnPrevention = () => {
  const [stats, setStats]                   = useState(null);
  const [highRiskClients, setHighRiskClients] = useState([]);
  const [retentionActions, setRetentionActions] = useState([]);
  const [trends, setTrends]                 = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selectedClient, setSelectedClient]  = useState(null);
  const [filterRisk, setFilterRisk]         = useState('all');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [activeTab, setActiveTab]           = useState('high-risk');

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, clientsRes, actionsRes, trendsRes] = await Promise.all([
        fetch('/api/churn/stats'),
        fetch('/api/churn/high-risk'),
        fetch('/api/churn/retention-actions'),
        fetch('/api/churn/trends')
      ]);
      setStats((await statsRes.json()).stats);
      setHighRiskClients((await clientsRes.json()).clients || []);
      setRetentionActions((await actionsRes.json()).actions || []);
      setTrends((await trendsRes.json()).trends || []);
    } catch (e) {
      console.error('Failed to fetch churn data:', e);
    } finally {
      setLoading(false);
    }
  };

  const riskBadge   = (c) => ({ critical:'bg-red-100 text-red-700', high:'bg-orange-100 text-orange-700', medium:'bg-yellow-100 text-yellow-700', low:'bg-green-100 text-green-700' }[c] || 'bg-gray-100 text-gray-600');
  const priorityBadge = (p) => ({ urgent:'bg-red-100 text-red-700', high:'bg-orange-100 text-orange-700', medium:'bg-blue-100 text-blue-700', low:'bg-gray-100 text-gray-600' }[p] || 'bg-gray-100 text-gray-600');
  const statusBadge = (s) => ({ pending:'bg-yellow-100 text-yellow-700', in_progress:'bg-blue-100 text-blue-700', completed:'bg-green-100 text-green-700', cancelled:'bg-gray-100 text-gray-600' }[s] || 'bg-gray-100 text-gray-600');

  const filteredClients = highRiskClients.filter(c => filterRisk === 'all' || c.risk_category === filterRisk);
  const filteredActions = retentionActions.filter(a => filterStatus === 'all' || a.status === filterStatus);

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto" />
        <p className="mt-4 text-gray-500 text-sm">Loading churn prevention dashboard...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 pb-10">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Churn Prevention</h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitor client risk and manage retention actions</p>
        </div>
        <button onClick={fetchDashboardData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label:'Total Monitored',      value: stats?.total_clients_monitored || 0, color:'text-blue-600',  Icon: Users },
          { label:'Critical Risk',        value: stats?.critical_risk_count     || 0, color:'text-red-600',   Icon: AlertTriangle },
          { label:'Pending Actions',      value: stats?.pending_actions         || 0, color:'text-orange-600',Icon: Clock },
          { label:'Successful Retentions',value: stats?.successful_retentions   || 0, color:'text-green-600', Icon: CheckCircle },
        ].map(({ label, value, color, Icon: Ic }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 font-medium">{label}</p>
              <p className={`text-3xl font-bold mt-1 ${color}`}>{value}</p>
            </div>
            <Ic className={`w-10 h-10 opacity-20 ${color}`} />
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex border-b border-gray-200 px-4">
          {[
            { key:'high-risk', label:'High-Risk Clients' },
            { key:'actions',   label:'Retention Actions' },
            { key:'trends',    label:'Trends & Analytics' },
          ].map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 mr-2 transition-colors ${
                activeTab === t.key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5">

          {/* High-Risk Clients */}
          {activeTab === 'high-risk' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">High-Risk Clients</h3>
                <select value={filterRisk} onChange={e=>setFilterRisk(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="all">All Risk Levels</option>
                  <option value="critical">Critical Only</option>
                  <option value="high">High Only</option>
                  <option value="medium">Medium Only</option>
                </select>
              </div>
              {filteredClients.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">No high-risk clients to display.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {['Client','Risk Score','Category','Account Owner','Lifetime Value','Tenure',''].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map(client => (
                        <tr key={client.client_id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-3">
                            <p className="font-medium text-gray-900">{client.client_name}</p>
                            <p className="text-xs text-gray-500">{client.email}</p>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2">
                              <div className="w-20 bg-gray-200 rounded-full h-2">
                                <div className="bg-red-500 h-2 rounded-full" style={{width:`${client.churn_risk_score}%`}} />
                              </div>
                              <span className="font-semibold text-xs">{client.churn_risk_score}%</span>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${riskBadge(client.risk_category)}`}>
                              {(client.risk_category||'').toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-gray-600">{client.account_owner || 'Unassigned'}</td>
                          <td className="py-3 px-3 text-gray-600">${(client.lifetime_value||0).toLocaleString()}</td>
                          <td className="py-3 px-3 text-gray-600">{Math.floor((client.client_tenure_days||0)/30)} mo</td>
                          <td className="py-3 px-3">
                            <button onClick={()=>setSelectedClient(client)}
                              className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100">
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Retention Actions */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Retention Actions</h3>
                <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500">
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              {filteredActions.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">No retention actions found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {['Client','Action','Priority','Status','Assigned To','Due Date','Risk'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredActions.map(action => (
                        <tr key={action.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-3 font-medium text-gray-900">{action.client_name}</td>
                          <td className="py-3 px-3">
                            <p className="font-medium">{action.action_title}</p>
                            <p className="text-xs text-gray-500">{action.action_type}</p>
                          </td>
                          <td className="py-3 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${priorityBadge(action.priority)}`}>
                              {(action.priority||'').toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(action.status)}`}>
                              {(action.status||'').replace('_',' ').toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-gray-600">{action.assigned_to_name || 'Unassigned'}</td>
                          <td className="py-3 px-3 text-gray-600">{new Date(action.due_date).toLocaleDateString()}</td>
                          <td className="py-3 px-3 font-semibold">{action.churn_risk_score}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Trends */}
          {activeTab === 'trends' && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Churn Risk Trends</h3>
              {trends.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">No trend data available yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        {['Week','Total','Critical','High','Medium','Low','Avg Risk'].map(h => (
                          <th key={h} className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {trends.map((t, i) => (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-3 text-gray-600">{new Date(t.week_start).toLocaleDateString()}</td>
                          <td className="py-3 px-3 font-semibold">{t.total_predictions}</td>
                          <td className="py-3 px-3 text-red-600 font-semibold">{t.critical_count}</td>
                          <td className="py-3 px-3 text-orange-600 font-semibold">{t.high_count}</td>
                          <td className="py-3 px-3 text-yellow-600 font-semibold">{t.medium_count}</td>
                          <td className="py-3 px-3 text-green-600 font-semibold">{t.low_count}</td>
                          <td className="py-3 px-3 font-semibold">{t.avg_risk_score}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Client Detail Modal */}
      {selectedClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={()=>setSelectedClient(null)} />
          <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 z-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Client Risk Details</h2>
              <button onClick={()=>setSelectedClient(null)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-gray-900">{selectedClient.client_name}</p>
                <p className="text-sm text-gray-500">{selectedClient.email}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Risk Score</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                    <div className="bg-red-500 h-3 rounded-full" style={{width:`${selectedClient.churn_risk_score}%`}} />
                  </div>
                  <span className="font-bold text-red-600">{selectedClient.churn_risk_score}%</span>
                </div>
              </div>
              {selectedClient.top_risk_factors?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Top Risk Factors</p>
                  <ul className="space-y-1">
                    {selectedClient.top_risk_factors.map((f, i) => (
                      <li key={i} className="text-sm text-gray-600 flex justify-between">
                        <span>{f.factor}</span>
                        <span className="font-medium">{typeof f.value === 'number' ? f.value.toFixed(2) : f.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  {selectedClient.pending_retention_actions || 0} pending retention action(s)
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChurnPrevention;
